import type { TFunction } from 'i18next';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  banFromReport,
  type CurrentUser,
  exitVolunteer,
  handleUserReport,
  handleVolunteerReport,
  liftBan,
  markNoteReviewed,
  moderationState,
  type ReportNote,
  type ReportStatus,
  setSuspension,
  type UserReportRow,
  type VolunteerReportRow,
} from '../api.ts';
import { formatWhen } from '../format.ts';
import { type Tone, useAnnounce } from '../hooks/useAnnounce.tsx';
import { useLoad } from '../hooks/useLoad.ts';
import { errorMessage } from '../messages.ts';

/**
 * Segnalazioni e ban, per chi coordina (mock 3, `c-mod`).
 *
 * "Senza questo, la registrazione obbligatoria per video e messaggi non serve a
 * niente."
 *
 * Tre cose che questa schermata **non** mostra, e sono le tre che contano:
 *
 *  - **chi ha segnalato un volontario.** Non è nascosto: non esiste. La
 *    segnalazione dice quale sessione, e su una chiamata anonima non c'è nulla
 *    dietro quella sessione (`docs/DECISIONI.md`, AC1);
 *  - **il numero di telefono di nessuno.** Nemmeno mascherato, nemmeno per chi
 *    coordina: chi è bloccato si riconosce dall'etichetta con cui compariva
 *    nel prodotto (sezione 5.2);
 *  - **il contenuto delle conversazioni.** Non è stato registrato e non è
 *    recuperabile, e la sessione non è modificabile né cancellabile da nessuno,
 *    coordinamento compreso (sezione 9quater).
 *
 * Le tre frasi che lo dicono stanno in fondo, dentro un `details`: erano tre
 * paragrafi sciolti in tre punti diversi — due dei quali attaccati a un altro
 * paragrafo — cioè prosa a piena larghezza prima e dopo il lavoro. Chi legge
 * poco legge le prime righe, e le prime righe devono essere le segnalazioni.
 *
 * Le quattro sezioni hanno la stessa forma: titolo, **quanti** elementi ci
 * sono, al più una riga che dice cosa governa la sezione, poi l'elenco. Con
 * cinque segnalazioni per parte, due sospesi e tre blocchi erano quaranta
 * pulsanti in quattro elenchi consecutivi senza nessuna gerarchia interna.
 */

type Props = {
  user: CurrentUser;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
};

/** Il clic su un pulsante di riga, che porta con sé chi l'ha ricevuto. */
type RowClick = React.MouseEvent<HTMLButtonElement>;

/**
 * Cosa fare quando la schermata si è fermata: dove rimettere il fuoco, e cosa
 * dire dopo averlo rimesso. In quest'ordine, e il motivo è nel corpo.
 */
type Pending = { focus: HTMLButtonElement | null; said?: { text: string; tone?: Tone } };

/** Cosa la scheda "Perché" sta per confermare, e su chi. */
type Asking =
  | { kind: 'suspend'; volunteerId: string; suspended: boolean; label: string }
  | { kind: 'ban'; reportId: string; label: string }
  | { kind: 'exit'; volunteerId: string; label: string };

/**
 * Cosa si sta guardando di un elenco di segnalazioni.
 *
 * `pending` è ciò che chiede una decisione — aperte e in esame — ed è la vista
 * di partenza: il server ne restituisce fino a cento di ogni tipo, archiviate
 * comprese, e una schermata che le mostra tutte insieme non distingue più il
 * lavoro da fare dallo storico di quello fatto.
 */
type Scope = 'pending' | 'all';

export function ModerationPage({ user, headingRef }: Props) {
  const { t, i18n } = useTranslation();

  /**
   * La regione live e il banner arrivano **insieme**, come un nodo solo.
   *
   * Questa schermata montava la regione live con il messaggio già dentro
   * (`{notice ? <p role="status">…`), che è il difetto AJ15 scritto al
   * contrario: una regione che compare già piena non viene annunciata da quasi
   * nessuna combinazione di browser e screen reader, e chi coordina senza
   * vedere sospendeva un volontario senza sentire l'esito — né quello riuscito,
   * né il fallimento. `useAnnounce` esiste perché montarne metà non sia
   * possibile.
   */
  const { say, announcer } = useAnnounce();
  const [busy, setBusy] = useState(false);
  /** Il modulo aperto: sospensione o blocco chiedono un perché, sempre. */
  const [asking, setAsking] = useState<Asking>();
  const [reason, setReason] = useState('');
  /** Cosa mostrano i due elenchi di segnalazioni. Uno per elenco: sono due code diverse. */
  const [volunteerScope, setVolunteerScope] = useState<Scope>('pending');
  const [userScope, setUserScope] = useState<Scope>('pending');
  /**
   * Il modulo del perché compare in cima, e il fuoco lo segue.
   *
   * Premendo "sospendi" su una riga in fondo, senza questo non succedeva niente
   * di percepibile: chi naviga da tastiera o con uno screen reader restava
   * dov'era, e poi confermava senza sapere su chi (WCAG 2.4.3, 3.3.2).
   */
  const askingHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (asking) askingHeadingRef.current?.focus();
  }, [asking]);

  /**
   * Il titolo della pagina, anche quando nessuno ce lo passa.
   *
   * È il bersaglio del fuoco al ritorno, e la schermata non può dipendere da un
   * `prop` facoltativo per non lasciare il fuoco sul corpo del documento. Il
   * chiamante che lo omette non è un test: è la galleria di sviluppo
   * (`gallery.tsx`), che monta questa schermata da sola.
   */
  const ownHeadingRef = useRef<HTMLHeadingElement>(null);
  const pageHeadingRef = headingRef ?? ownHeadingRef;

  /**
   * Dopo un'azione, il fuoco torna dove la persona lo aveva lasciato — e
   * **solo dopo** si annuncia l'esito.
   *
   * Erano due difetti distinti con la stessa causa: la schermata cambiava
   * sotto il fuoco senza dire a nessuno dove fosse finito.
   *
   *  - chiudendo la scheda "Perché", il fuoco cadeva sul `<body>`: lo screen
   *    reader taceva e il Tab ripartiva dall'inizio della pagina (WCAG 2.4.3).
   *    È AJ16, che elencava la scheda di una conversazione, quella di un ente e
   *    l'annullamento nella blacklist — non questa;
   *  - le azioni di riga (prendi in carico, archivia, riapri, segna come
   *    rivista, sblocca) si disabilitano mentre ci sta sopra il fuoco, perché
   *    sono loro a impostare `busy`: un pulsante disabilitato esce dal percorso
   *    di tabulazione e scarica il fuoco sul corpo del documento (X3, U2).
   *    Restano disabilitate — sono azioni alternative sulla stessa riga, e
   *    `busy` è di pagina: cambiare etichetta le farebbe dire "Salvataggio…"
   *    tutte insieme — ma il fuoco torna dove era.
   *
   * Dove torna dipende da cosa è successo. Se il pulsante di partenza è ancora
   * lì e di nuovo premibile, si torna esattamente lì: è l'unico posto da cui la
   * persona sa riprendere. Se non c'è più — una persona appena riammessa esce
   * dall'elenco dei sospesi, una segnalazione presa in carico cambia i propri
   * pulsanti — si torna al titolo della pagina, che è subito sopra il banner
   * con l'esito: come nello storico e nella scheda di un ente.
   *
   * **L'esito si annuncia dopo aver spostato il fuoco, non prima.** Uno
   * spostamento di fuoco interrompe la coda di parlato di NVDA, JAWS e
   * VoiceOver: dicendolo prima, lo screen reader cominciava "Volontario
   * sospeso. La conversazione in corso prosegue…" e veniva zittito dal titolo
   * su cui il fuoco stava arrivando — cioè si perdeva la frase che porta il
   * vincolo 5, l'informazione con più peso della schermata. Non serve un timer:
   * `focus()` è sincrono qui dentro, mentre `say` programma un disegno che
   * arriva dopo.
   */
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const pending = useRef<Pending | null>(null);
  /**
   * Il perché manca, e bisogna dirlo.
   *
   * Il segnale era **soltanto** il pulsante disabilitato, e un pulsante
   * disabilitato si attraversa in silenzio: chi non vede arrivava in fondo
   * alla scheda senza che niente gli avesse detto che mancava qualcosa
   * (WCAG 3.3.2). La stessa forma della scheda di un ente, che ce l'aveva già.
   */
  const [reasonMissing, setReasonMissing] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  // Senza elenco di dipendenze, di proposito: il lavoro va fatto al primo
  // disegno in cui la schermata si è **fermata** (`!busy && !asking`), e quel
  // momento non coincide con il cambio di nessuna singola variabile.
  useEffect(() => {
    const job = pending.current;
    if (!job || busy || asking) return;
    pending.current = null;

    const node = job.focus;
    if (node?.isConnected && !node.disabled) node.focus();
    else pageHeadingRef.current?.focus();

    if (job.said) say(job.said.text, job.said.tone);
  });

  /** Chi ha aperto la scheda, per saperci tornare. */
  const ask = (event: RowClick, what: Asking): void => {
    // Un ritorno rimasto in sospeso da un giro precedente non vale più.
    pending.current = null;
    openerRef.current = event.currentTarget;
    /*
      E il modulo riparte **vuoto**: il motivo scritto per una persona e non
      confermato restava nel campo aprendo la scheda di un'altra, e bastava
      premere perché quel perché finisse nel registro accessi con il nome
      sbagliato. Il registro è append-only: quella riga non si corregge (AS7).
    */
    setReason('');
    setReasonMissing(false);
    setAsking(what);
  };

  const cancelAsking = (): void => {
    pending.current = { focus: openerRef.current };
    setAsking(undefined);
    setReason('');
    setReasonMissing(false);
  };

  /**
   * `Esc` chiude la scheda, esattamente come "Annulla".
   *
   * Non è una modale e non c'è trappola di fuoco, quindi non era una
   * violazione: era attrito, e cadeva tutto su chi dipende dalla tastiera —
   * per uscire da una scheda aperta per sbaglio bisognava tabulare fino in
   * fondo. Passa da `cancelAsking`, così il ritorno del fuoco è quello di
   * AJ16 e non una seconda strada che un giorno diverge.
   */
  const onEscape = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Escape' || busy) return;
    event.stopPropagation();
    cancelAsking();
  };

  const read = useCallback((signal: AbortSignal) => moderationState({ signal }), []);
  const {
    data: state,
    failed,
    reload,
  } = useLoad(read, {
    onError: (error) => say(`${t('moderation.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const when = (iso: string | null | undefined): string =>
    formatWhen(iso, i18n.resolvedLanguage, user.tenant?.timezone);

  /**
   * Un'azione su una riga: la esegue, ne annuncia l'esito, e ricorda da quale
   * pulsante era partita.
   *
   * Erano tre copie delle stesse otto righe (`handle`, `review`, `unblock`):
   * guardia su `busy`, `setBusy(true)`, l'esito, il fallimento con il suo
   * prefisso, `reload`, `setBusy(false)`. La terza copia è il momento in cui si
   * estrae, ed è anche il punto in cui il ritorno del fuoco poteva essere
   * dimenticato in una delle tre.
   */
  const act = async (
    event: RowClick,
    work: () => Promise<string>,
    failedPrefix: string,
  ): Promise<void> => {
    if (busy) return;
    const opener = event.currentTarget;
    setBusy(true);
    try {
      const said = await work();
      pending.current = { focus: opener, said: { text: said } };
      await reload();
    } catch (error) {
      pending.current = {
        focus: opener,
        said: { text: `${failedPrefix} ${errorMessage(t, error)}`, tone: 'danger' },
      };
    } finally {
      setBusy(false);
    }
  };

  const handle = (
    event: RowClick,
    kind: 'volunteer' | 'user',
    id: string,
    status: ReportStatus,
  ): Promise<void> =>
    act(
      event,
      async () => {
        if (kind === 'volunteer') await handleVolunteerReport(id, status);
        else await handleUserReport(id, status);
        return t('moderation.handled');
      },
      t('moderation.handle_failed'),
    );

  const confirm = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const current = asking;
    const why = reason.trim();
    if (!current || busy) return;
    /**
     * Il perché manca: **si dice**, non si tace.
     *
     * Era un `return` muto dietro un pulsante disabilitato, ed è la modifica
     * che quel commento prevedeva. Togliere `disabled` senza mettere questo
     * avrebbe lasciato un pulsante che non fa niente quando lo premi — che è
     * peggio del difetto di prima, non meglio.
     *
     * Il fuoco va **dove manca il testo**: annunciare "serve una motivazione"
     * lasciando il fuoco sul pulsante appena premuto vuol dire dire a chi non
     * vede che manca qualcosa senza portarlo dove manca (WCAG 3.3.1). Prima il
     * fuoco e poi l'annuncio, come vuole AP4: `focus()` è sincrono, `say`
     * programma un disegno che arriva dopo, quindi lo spostamento non zittisce
     * la frase.
     *
     * La regola resta comunque qui e non sul pulsante: una sospensione **non
     * si scrive senza un perché**, e quel perché finisce nel registro accessi
     * dell'ente.
     */
    if (!why) {
      setReasonMissing(true);
      reasonRef.current?.focus();
      say(t('moderation.reason_required'), 'danger');
      return;
    }
    setBusy(true);
    let said = '';
    try {
      if (current.kind === 'suspend') {
        const result = await setSuspension(current.volunteerId, {
          suspended: current.suspended,
          reason: why,
        });
        /**
         * Se sta parlando con qualcuno adesso, va detto.
         *
         * È l'unica cosa che il pulsante non fa: quella conversazione prosegue
         * fino alla fine, perché il sistema non interrompe mai una sessione
         * attiva (vincolo 5, AC12).
         */
        said = [
          current.suspended ? t('moderation.suspended_msg') : t('moderation.reinstated_msg'),
          result.sessionStillRunning ? t('moderation.suspend_session_note') : '',
        ]
          .filter(Boolean)
          .join(' ');
      } else if (current.kind === 'exit') {
        const result = await exitVolunteer(current.volunteerId, { reason: why });
        // Come per la sospensione: una conversazione in corso prosegue.
        said = [
          t('moderation.exited_msg'),
          result.sessionStillRunning ? t('moderation.suspend_session_note') : '',
        ]
          .filter(Boolean)
          .join(' ');
      } else {
        const result = await banFromReport({ reportId: current.reportId, reason: why });
        // Anche qui: se sta parlando con qualcuno adesso, quella conversazione
        // prosegue fino alla fine (vincolo 5).
        said = [
          t('moderation.banned_msg'),
          result.sessionStillRunning ? t('moderation.ban_session_note') : '',
        ]
          .filter(Boolean)
          .join(' ');
      }
      /*
        Il pulsante da cui si era partiti può non esistere più — chi è appena
        stato riammesso esce dall'elenco dei sospesi — quindi il bersaglio è il
        titolo, e `focus: null` lo dice invece di scoprirlo con `isConnected`.
      */
      pending.current = { focus: null, said: { text: said } };
      setAsking(undefined);
      setReason('');
      await reload();
    } catch (error) {
      const prefix =
        current.kind === 'suspend'
          ? t('moderation.suspend_failed')
          : current.kind === 'exit'
            ? t('moderation.exit_failed')
            : t('moderation.ban_failed');
      say(`${prefix} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const review = (event: RowClick, id: string): Promise<void> =>
    act(
      event,
      async () => {
        await markNoteReviewed(id);
        return t('moderation.note_reviewed_ok');
      },
      t('moderation.note_review_failed'),
    );

  const unblock = (event: RowClick, id: string): Promise<void> =>
    act(
      event,
      async () => {
        await liftBan(id);
        return t('moderation.unbanned_msg');
      },
      t('moderation.unban_failed'),
    );

  const volunteerReports = inScope(state?.volunteerReports, volunteerScope);
  const userReports = inScope(state?.userReports, userScope);

  return (
    <main className="page" id="main" tabIndex={-1}>
      {/*
        Un nome solo, e viene dalla barra.

        Il titolo diceva "Segnalazioni e ban" e la voce di navigazione
        "Segnalazioni": due nomi per la stessa schermata, e chi arriva dalla
        barra deve riconoscere dove è finito. Non è una copia della stringa: è
        **la stessa chiave**, così i due nomi non possono più divergere. La
        riga di descrizione sotto è sparita — elencava le sezioni che si
        vedono da sé, con i loro titoli e i loro conteggi.
      */}
      <h2 ref={pageHeadingRef} tabIndex={-1}>
        {t('nav.moderation')}
      </h2>

      {announcer}

      {/*
        Un supervisore legge le segnalazioni sui volontari — "arriva al
        coordinatore **e al supervisore**" (sezione 7quater) — e nient'altro.
        Dirlo evita di far cercare pulsanti che il suo ruolo non ha.
      */}
      {state && !state.canModerate ? <p className="note">{t('moderation.read_only')}</p> : null}

      {asking ? (
        <section className="card" aria-labelledby="reason-heading" onKeyDown={onEscape}>
          {/* Il titolo dice **su chi**: senza, si conferma alla cieca. */}
          <h3 id="reason-heading" ref={askingHeadingRef} tabIndex={-1}>
            {asking.kind === 'ban'
              ? t('moderation.ban_named', { name: asking.label })
              : asking.kind === 'exit'
                ? t('moderation.exit_named', { name: asking.label })
                : asking.suspended
                  ? t('moderation.suspend_named', { name: asking.label })
                  : t('moderation.reinstate_named', { name: asking.label })}
          </h3>
          {/*
            `noValidate`, e non è un dettaglio: senza, il `required` sul campo
            fa **bloccare la submit dal browser**, con una nuvoletta nativa che
            non è tradotta, non è annunciata e sparisce da sola. Il gestore non
            veniva nemmeno chiamato — il pulsante sembrava semplicemente non
            fare niente, cioè il difetto di prima con un'altra faccia. Il
            `required` resta perché è quello che dice all'albero di
            accessibilità che il campo è obbligatorio; a dirlo a parole ci
            pensa il prodotto. È la stessa scelta della schermata di accesso.
          */}
          {/* Irreversibile: si dice prima, non dopo. */}
          {asking.kind === 'exit' ? (
            <p className="note" id="moderation-exit-explain">
              {t('moderation.exit_explain')}
            </p>
          ) : null}
          <form onSubmit={(event) => void confirm(event)} noValidate>
            <div className="field">
              <label htmlFor="moderation-reason">{t('moderation.reason_label')}</label>
              <textarea
                id="moderation-reason"
                ref={reasonRef}
                rows={2}
                maxLength={500}
                required
                aria-describedby={
                  asking.kind === 'exit'
                    ? 'moderation-exit-explain moderation-reason-hint'
                    : 'moderation-reason-hint'
                }
                aria-invalid={reasonMissing ? true : undefined}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonMissing(false);
                }}
              />
              {/*
                Dice due cose, e servono entrambe: che è obbligatorio — il
                pulsante non lo dice più, e non lo diceva comunque a chi non
                vede — e **dove finisce quel testo**, che è l'informazione per
                cui vale la pena scriverlo bene.
              */}
              <p className="muted" id="moderation-reason-hint">
                {t('moderation.reason_hint')}
              </p>
            </div>
            {/*
              Cambia etichetta mentre lavora, **non** si disabilita.

              Il fuoco e' su questo pulsante nell'istante in cui si preme:
              disabilitarlo lo toglie dal percorso di tabulazione e scarica il
              fuoco sul `<body>` proprio mentre si aspetta un esito, cioe' il
              momento in cui serve di piu' sapere dove si e' (X3, U2). E' la
              trappola che `SupervisionPanel` evita di proposito, e qui era
              aperta. Il doppio tocco lo ferma la guardia dentro `confirm`.

              E **non si disabilita nemmeno a motivo vuoto**, che era l'altra
              meta' del difetto: un pulsante disabilitato si attraversa in
              silenzio, quindi era l'unico segnale del fatto che il motivo
              serve, e non arrivava a chi non vede (WCAG 3.3.2). Adesso si
              preme, e premendolo si sente cosa manca. Un blocco senza un
              perche' lo ferma la guardia dentro `confirm`, che ha la sua
              prova (AP4).

              È l'unica `--primary` della schermata, e c'è solo mentre la
              scheda è aperta: tutto il resto sono azioni di riga.
            */}
            <div className="actions">
              <button type="submit" className="button button--primary">
                {busy
                  ? t('moderation.saving')
                  : asking.kind === 'ban'
                    ? t('moderation.ban_confirm')
                    : asking.kind === 'exit'
                      ? t('moderation.exit_confirm')
                      : asking.suspended
                        ? t('moderation.suspend')
                        : t('moderation.reinstate')}
              </button>
              {/* Annullare è servizio: non deve pesare quanto ciò che decide. */}
              <button type="button" className="button button--quiet" onClick={cancelAsking}>
                {t('report_user.cancel')}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {/*
        L'avviso automatico per segnalazioni ripetute (sezione 7quater).

        È un conteggio derivato, non una riga in tabella: si spegne da solo
        quando le segnalazioni vengono chiuse, che è il comportamento giusto per
        un avviso che chiede di fare qualcosa (AC5).
      */}
      {state && state.flagged.length > 0 ? (
        <section className="card" aria-labelledby="flagged-heading">
          <h3 id="flagged-heading">{t('moderation.flagged_title')}</h3>
          {state.flagged.map((entry) => (
            <p key={entry.volunteerId} className="banner banner--danger">
              {t(entry.open === 1 ? 'moderation.flagged_one' : 'moderation.flagged_other', {
                name: entry.displayName ?? t('moderation.unknown_person'),
                count: entry.open,
              })}
            </p>
          ))}
        </section>
      ) : null}

      <section className="card" aria-labelledby="on-volunteers-heading">
        <div className="section-head">
          <h3 id="on-volunteers-heading">{t('moderation.on_volunteers')}</h3>
          {/* Quante ne ha davanti chi guarda: non c'era nessun numero, da nessuna parte. */}
          {state ? (
            <p className="count">
              {t('moderation.count_reports', { count: volunteerReports.length })}
            </p>
          ) : null}
        </div>
        <p className="card__sub">{t('moderation.on_volunteers_hint')}</p>

        <ScopeFilter id="volunteer-reports" scope={volunteerScope} onChange={setVolunteerScope} />

        {/*
        "Caricamento…" **finché non è fallito**.

        Senza `failed`, una lettura andata storta lasciava il banner d'errore e
        "Caricamento…" nella stessa schermata, per sempre: due affermazioni che
        si contraddicono, e la seconda letta da chi non vede solo se torna
        indietro con lo screen reader. È il difetto che `useLoad` esiste per
        chiudere, e convertendo questa schermata era rientrato dalla finestra
        (AM5, AO10).

        E adesso ce l'hanno **tutte e quattro** le sezioni: era solo qui, e le
        altre tre durante il caricamento mostravano titolo, testi e un elenco
        vuoto — cioè affermavano "non c'è niente" prima di saperlo.
      */}
        {!state && !failed ? <Loading t={t} /> : null}
        <EmptyList
          state={state}
          shown={volunteerReports.length}
          total={state?.volunteerReports.length ?? 0}
          scope={volunteerScope}
          emptyKey="moderation.on_volunteers_empty"
        />

        <ul className="plain-list">
          {volunteerReports.map((report) => (
            <li key={report.id} className="report">
              <p className="row__title">
                {t('moderation.reported_volunteer', {
                  name: report.volunteer.displayName ?? t('moderation.unknown_person'),
                })}
              </p>
              <p className="row__meta">
                {sessionLine(report, t, when)} · {t(`moderation.status_${report.status}`)}
              </p>
              <p>{t(`moderation.reason_${report.reason}`)}</p>
              {report.text ? <p>{report.text}</p> : null}
              {/*
                Il debriefing della conversazione: l'unica traccia di cosa quella
                sessione è stata (AC2). Non c'è registrazione, non c'è
                trascrizione, non c'è contenuto.
              */}
              <DebriefCard
                note={report.note}
                onReview={(event, id) => void review(event, id)}
                busy={busy}
                when={when}
              />
              <div className="row__actions">
                {statusButtons(
                  report.status,
                  (event, status) => void handle(event, 'volunteer', report.id, status),
                  t,
                  busy,
                  when(report.createdAt),
                )}
                {state?.canModerate ? (
                  <button
                    type="button"
                    className="button button--danger"
                    disabled={busy}
                    /* Su chi, nel nome per esteso: a schermo il verbo basta,
                       perché la riga si apre con il nome del volontario. */
                    aria-label={t('moderation.suspend_named', {
                      name: report.volunteer.displayName ?? t('moderation.unknown_person'),
                    })}
                    onClick={(event) =>
                      ask(event, {
                        kind: 'suspend',
                        volunteerId: report.volunteer.id,
                        suspended: true,
                        label: report.volunteer.displayName ?? t('moderation.unknown_person'),
                      })
                    }
                  >
                    {t('moderation.suspend_short')}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="card" aria-labelledby="on-users-heading">
        <div className="section-head">
          <h3 id="on-users-heading">{t('moderation.on_users')}</h3>
          {state ? (
            <p className="count">{t('moderation.count_reports', { count: userReports.length })}</p>
          ) : null}
        </div>

        <ScopeFilter id="user-reports" scope={userScope} onChange={setUserScope} />

        {!state && !failed ? <Loading t={t} /> : null}
        <EmptyList
          state={state}
          shown={userReports.length}
          total={state?.userReports.length ?? 0}
          scope={userScope}
          emptyKey="moderation.on_users_empty"
        />

        <ul className="plain-list">
          {userReports.map((report) => (
            <li key={report.id} className="report">
              <p className="row__title">
                {report.reportedBy
                  ? t('moderation.reported_by', { name: report.reportedBy })
                  : t('moderation.unknown_person')}
              </p>
              <p className="row__meta">
                {sessionLine(report, t, when)} · {t(`moderation.status_${report.status}`)}
              </p>
              <p>{t(`moderation.user_reason_${report.reason}`)}</p>
              {report.text ? <p>{report.text}</p> : null}
              <DebriefCard
                note={report.note}
                onReview={(event, id) => void review(event, id)}
                busy={busy}
                when={when}
              />
              <div className="row__actions">
                {statusButtons(
                  report.status,
                  (event, status) => void handle(event, 'user', report.id, status),
                  t,
                  busy,
                  when(report.createdAt),
                )}
                {report.bannable ? (
                  <button
                    type="button"
                    className="button button--danger"
                    disabled={busy}
                    aria-label={t('moderation.ban_named', { name: banLabel(report, t) })}
                    onClick={(event) =>
                      ask(event, {
                        kind: 'ban',
                        reportId: report.id,
                        label: banLabel(report, t),
                      })
                    }
                  >
                    {t('moderation.ban_confirm')}
                  </button>
                ) : (
                  /* Anonima: non c'è nessun account da bloccare, e si dice
                     invece di lasciare un pulsante che non funziona (AC16). */
                  <span className="muted">{t('moderation.ban_anonymous')}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="card" aria-labelledby="suspended-heading">
        <div className="section-head">
          <h3 id="suspended-heading">{t('moderation.suspended_title')}</h3>
          {state ? (
            <p className="count">
              {t('moderation.count_people', { count: state.suspended.length })}
            </p>
          ) : null}
        </div>

        {!state && !failed ? <Loading t={t} /> : null}
        {state && state.suspended.length === 0 ? (
          <div className="empty">
            <p>{t('moderation.suspended_empty')}</p>
          </div>
        ) : null}

        <ul className="plain-list">
          {state?.suspended.map((person) => (
            <li key={person.id} className="row">
              <div>
                <p className="row__title">{person.displayName}</p>
                <p className="row__meta">
                  {t('moderation.suspended_since', { when: when(person.suspendedAt) })}
                </p>
              </div>
              <div className="row__actions">
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={(event) =>
                    ask(event, {
                      kind: 'suspend',
                      volunteerId: person.id,
                      suspended: false,
                      label: person.displayName,
                    })
                  }
                >
                  {t('moderation.reinstate')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/*
        La squadra, per registrare l'uscita di chi non collabora più (sezione
        7quinquies, BW2). Solo per chi coordina, come i sospesi: al supervisore
        e al fornitore in assistenza non serve, e la rotta li rifiuta comunque.
        Chi guarda non compare: non si registra la propria uscita.
      */}
      {state?.canModerate ? (
        <section className="card" aria-labelledby="team-heading">
          <div className="section-head">
            <h3 id="team-heading">{t('moderation.team_title')}</h3>
            <p className="count">
              {t('moderation.count_people', {
                count: others(state.team, user.id).length,
              })}
            </p>
          </div>
          <p className="card__sub">{t('moderation.team_hint')}</p>
          {others(state.team, user.id).length === 0 ? (
            <div className="empty">
              <p>{t('moderation.team_empty')}</p>
            </div>
          ) : null}
          <ul className="plain-list">
            {others(state.team, user.id).map((member) => (
              <li key={member.id} className="row">
                <div>
                  <p className="row__title">{member.displayName}</p>
                  <p className="row__meta">
                    {t(`platform.role_${member.role}`, { defaultValue: member.role })}
                  </p>
                </div>
                <div className="row__actions">
                  <button
                    type="button"
                    className="button button--danger"
                    disabled={busy}
                    aria-label={t('moderation.exit_named', { name: member.displayName })}
                    onClick={(event) =>
                      ask(event, {
                        kind: 'exit',
                        volunteerId: member.id,
                        label: member.displayName,
                      })
                    }
                  >
                    {t('moderation.exit_short')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="card" aria-labelledby="bans-heading">
        <div className="section-head">
          <h3 id="bans-heading">{t('moderation.bans_title')}</h3>
          {state ? (
            <p className="count">{t('moderation.count_accounts', { count: state.bans.length })}</p>
          ) : null}
        </div>
        <p className="card__sub">{t('moderation.bans_hint')}</p>

        {!state && !failed ? <Loading t={t} /> : null}
        {state && state.bans.length === 0 ? (
          <div className="empty">
            <p>{t('moderation.bans_empty')}</p>
          </div>
        ) : null}

        <ul className="plain-list">
          {state?.bans.map((ban) => (
            <li key={ban.id} className="row">
              <div>
                <p className="row__title">{guestLabel(ban.label, t)}</p>
                <p className="row__meta">
                  {[
                    t('moderation.ban_since', { when: when(ban.createdAt) }),
                    ban.expiresAt
                      ? t('moderation.ban_until', { when: when(ban.expiresAt) })
                      : t('moderation.ban_forever'),
                  ].join(' · ')}
                </p>
              </div>
              <div className="row__actions">
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  aria-label={t('moderation.unban_named', { name: guestLabel(ban.label, t) })}
                  onClick={(event) => void unblock(event, ban.id)}
                >
                  {t('moderation.unban')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/*
        Le tre cose che questa schermata non mostra, in un posto solo.

        Erano tre paragrafi sciolti — due attaccati ad altri due, e uno di
        centosessanta caratteri in chiusura di pagina — cioè spiegazioni messe
        dove si lavora. Qui restano nell'albero del documento e le trova chi le
        cerca, che è chi si sta chiedendo se il volontario verrà a sapere della
        segnalazione, o se quella sessione si può cancellare (non si può, da
        nessun ruolo).
      */}
      <details className="more">
        <summary>{t('moderation.about_title')}</summary>
        <p className="muted">{t('moderation.reports_note')}</p>
        <p className="muted">{t('moderation.no_number_note')}</p>
        <p className="muted">{t('moderation.session_immutable')}</p>
      </details>
    </main>
  );
}

/**
 * Cosa mostra un elenco di segnalazioni.
 *
 * Un filtro solo, e la vista di partenza è il lavoro da fare: il server manda
 * fino a cento segnalazioni per tipo, archiviate comprese, e senza niente che
 * le separi le tre aperte stavano in mezzo a quelle chiuse un mese fa.
 */
function ScopeFilter({
  id,
  scope,
  onChange,
}: {
  id: string;
  scope: Scope;
  onChange: (scope: Scope) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="filters">
      <div className="field field--inline">
        <label htmlFor={`${id}-scope`}>{t('moderation.filter_status')}</label>
        <select
          id={`${id}-scope`}
          value={scope}
          onChange={(event) => onChange(event.target.value === 'all' ? 'all' : 'pending')}
        >
          <option value="pending">{t('moderation.filter_pending')}</option>
          <option value="all">{t('moderation.filter_all')}</option>
        </select>
      </div>
    </div>
  );
}

/**
 * "Non c'è niente" si dice **solo quando si sa**, e dice anche perché.
 *
 * Con il filtro su "da gestire" un elenco vuoto non vuol dire che non sia mai
 * arrivata una segnalazione: vuol dire che sono state tutte chiuse, ed è una
 * frase diversa — altrimenti chi ha appena archiviato l'ultima crede di aver
 * perso qualcosa.
 */
function EmptyList({
  state,
  shown,
  total,
  scope,
  emptyKey,
}: {
  state: unknown;
  shown: number;
  total: number;
  scope: Scope;
  emptyKey: string;
}) {
  const { t } = useTranslation();
  if (!state || shown > 0) return null;
  return (
    <div className="empty">
      <p>{scope === 'pending' && total > 0 ? t('moderation.empty_pending') : t(emptyKey)}</p>
    </div>
  );
}

/**
 * Sto ancora caricando, e non sto dicendo che non c'è niente.
 *
 * Tre barre grigie al posto delle righe che stanno arrivando: dicono "qui
 * comparirà un elenco" senza affermare quanto sarà lungo. Il segnaposto è
 * `aria-hidden` e a dirlo a chi non guarda è il testo, una volta sola.
 */
function Loading({ t }: { t: TFunction }) {
  return (
    <>
      <p className="visually-hidden">{t('moderation.loading')}</p>
      <div aria-hidden="true">
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--half" />
      </div>
    </>
  );
}

/**
 * Le segnalazioni che si stanno guardando, nell'ordine in cui si lavorano.
 *
 * Non c'era nessun ordinamento e nessun filtro: il server le manda per data,
 * quindi una segnalazione aperta di stamattina poteva stare sotto tre chiuse
 * di ieri. Prima le aperte — non le ha prese in carico nessuno — poi quelle in
 * esame, poi le archiviate; a parità, la più recente.
 */
/**
 * La squadra tranne chi guarda: la propria uscita non si registra. `?? []`
 * perché è l'unico campo della risposta aggiunto dopo, e una risposta che non
 * lo porta non deve far cadere l'intera schermata.
 */
function others<T extends { id: string }>(team: T[] | undefined, self: string): T[] {
  return (team ?? []).filter((member) => member.id !== self);
}

function inScope<T extends { status: ReportStatus; createdAt: string }>(
  rows: T[] | undefined,
  scope: Scope,
): T[] {
  const rank = (status: ReportStatus): number =>
    status === 'open' ? 0 : status === 'reviewing' ? 1 : 2;
  return (rows ?? [])
    .filter((row) => scope === 'all' || row.status !== 'closed')
    .sort((a, b) => rank(a.status) - rank(b.status) || b.createdAt.localeCompare(a.createdAt));
}

/**
 * I pulsanti di stato di una segnalazione.
 *
 * Tre stati e non due: "in esame" esiste perché in un ente con più
 * coordinatori serve sapere che qualcuno ci sta già lavorando.
 *
 * Due pesi, non uno solo. Erano tre pulsanti identici — "prendi in carico",
 * "archivia", "riapri" — cioè tre uscite equivalenti su una riga che ne ha una
 * sola sensata per volta: il passo che ci si aspetta porta il bordo, l'altro è
 * servizio. E il nome per esteso — quale segnalazione — resta in `aria-label`:
 * a schermo, ripetuto per dieci righe, era una colonna di frasi lunghe uguali.
 */
function statusButtons(
  status: ReportStatus,
  onChange: (event: RowClick, status: ReportStatus) => void,
  t: (key: string, options?: Record<string, string>) => string,
  busy: boolean,
  /**
   * Quando la segnalazione è arrivata.
   *
   * Serve al **nome** del pulsante: in un elenco di dieci segnalazioni,
   * "archivia" ripetuto dieci volte è indistinguibile per chi le scorre con
   * uno screen reader (WCAG 2.4.6).
   */
  when: string,
) {
  return (
    <>
      {status === 'open' ? (
        <button
          type="button"
          className="button"
          disabled={busy}
          aria-label={t('moderation.take_named', { when })}
          onClick={(event) => onChange(event, 'reviewing')}
        >
          {t('moderation.take')}
        </button>
      ) : null}
      {status !== 'closed' ? (
        <button
          type="button"
          className={status === 'open' ? 'button button--quiet' : 'button'}
          disabled={busy}
          aria-label={t('moderation.close_named', { when })}
          onClick={(event) => onChange(event, 'closed')}
        >
          {t('moderation.close')}
        </button>
      ) : (
        <button
          type="button"
          className="button button--quiet"
          disabled={busy}
          aria-label={t('moderation.reopen_named', { when })}
          onClick={(event) => onChange(event, 'open')}
        >
          {t('moderation.reopen')}
        </button>
      )}
    </>
  );
}

/**
 * Come si nomina chi si sta per bloccare.
 *
 * L'etichetta con cui compariva nel prodotto, composta qui con la traduzione —
 * `Ospite 47` — e mai un numero nudo: si sta decidendo di bloccare una persona,
 * e un numero non è un nome. Il telefono non passa da qui e non deve passarci.
 */
function guestLabel(
  label: { custom: string | null; number: number } | null,
  t: (key: string, options?: Record<string, string | number>) => string,
): string {
  if (!label) return t('moderation.unknown_person');
  return label.custom ?? t('call.guest_label', { number: label.number });
}

function banLabel(
  report: UserReportRow,
  t: (key: string, options?: Record<string, string | number>) => string,
): string {
  return report.session?.anonymous === false
    ? t('moderation.session_registered')
    : t('moderation.unknown_person');
}

/**
 * Il debriefing della conversazione segnalata.
 *
 * È la ragione per cui la fase 6 lo ha costruito (AC2): senza, una segnalazione
 * è la parola di uno contro il silenzio dell'altro. Quando non c'è, va detto —
 * il debriefing è facoltativo, e la sua assenza è a sua volta un'informazione.
 */
function DebriefCard({
  note,
  onReview,
  busy,
  when,
}: {
  note: ReportNote | null;
  onReview: (event: RowClick, id: string) => void;
  busy: boolean;
  when: (iso: string | null | undefined) => string;
}) {
  const { t } = useTranslation();

  if (!note) return <p className="muted">{t('moderation.note_none')}</p>;

  return (
    <div className="note">
      <p className="row__title">{t('moderation.note_title')}</p>
      <p className="muted">
        {t('moderation.note_line', {
          risk: t(note.riskLevel ? `debrief.risk_${note.riskLevel}` : 'debrief.risk_none'),
          age: t(note.ageBand ? `debrief.age_${note.ageBand}` : 'debrief.age_none'),
          topic: t(note.topic ? `debrief.topic_${note.topic}` : 'debrief.topic_none'),
        })}
      </p>
      {note.text ? <p>{note.text}</p> : null}
      {note.referredExternally ? <p className="muted">{t('moderation.note_referred')}</p> : null}
      {note.supervisorAlerted ? <p className="muted">{t('moderation.note_supervisor')}</p> : null}
      {note.reviewedAt ? (
        <p className="muted">{t('moderation.note_reviewed', { when: when(note.reviewedAt) })}</p>
      ) : (
        <button
          type="button"
          className="button button--quiet"
          disabled={busy}
          onClick={(event) => onReview(event, note.id)}
        >
          {t('moderation.note_review')}
        </button>
      )}
    </div>
  );
}

/** La riga della sessione: metadati, mai contenuto e mai un identificatore. */
function sessionLine(
  report: VolunteerReportRow | UserReportRow,
  t: (key: string, options?: Record<string, string>) => string,
  when: (iso: string | null | undefined) => string,
): string {
  if (!report.session) return t('moderation.session_anonymous');
  return t('moderation.session_line', {
    channel: report.session.anonymous
      ? t('moderation.session_anonymous')
      : t('moderation.session_registered'),
    when: when(report.session.requestedAt),
  });
}
